// Exportable de viajes del proveedor (LQ-1, ADR-026 §6.2 y "Construccion de
// LQ-1"). Logica pura, sin React ni fetch: rutas, etiquetas y formatos de la
// vista previa, con test al lado.
//
// El exportable es el mismo que baja la planta: el proveedor y la planta
// concilian sobre el mismo archivo. Aca no se recalcula nada: los numeros
// vienen sellados de la API y solo se pintan. Sin montos (regla 7) ni datos
// de personas: metros, minutos y cantidades.
//
// Los kilometros salen de metros enteros con aritmetica entera: 12345 m es
// "12,345" exacto, sin el redondeo de un punto flotante.
import type { BillableOutcome, SettlementPeriodKind, TripExportPeriod, TripExportResponse, TripExportRow } from "@ucorp/contracts";
import { formatCalendarDate } from "./documents.js";
import { VEHICLE_TYPE_LABEL } from "./fleet.js";

export type TripExportFormat = "json" | "csv" | "xlsx";

/** Filas de la vista previa; el archivo trae todas. */
export const TRIP_EXPORT_PREVIEW_ROWS = 50;

const EXTENSION: Record<TripExportFormat, string> = { json: "", csv: ".csv", xlsx: ".xlsx" };

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Fecha de calendario valida (AAAA-MM-DD que existe), como la pide la API. */
export function isCalendarDate(value: string): boolean {
  const match = CALENDAR_DATE.exec(value);
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;
  return day <= days;
}

/**
 * Ruta del exportable del proveedor. Sin providerId: la API lo saca de la
 * habilitacion y rechaza la query que lo trae (400).
 */
export function tripExportPath(siteId: string, date: string, format: TripExportFormat): string {
  const query = new URLSearchParams({ date }).toString();
  return `/sites/${encodeURIComponent(siteId)}/provider/settlements/trip-export${EXTENSION[format]}?${query}`;
}

/** Ruta para leer el dia operativo de hoy de la planta: la bandeja sin fecha, una sola fila. */
export function operationalTodayPath(siteId: string): string {
  return `/sites/${encodeURIComponent(siteId)}/provider/trips?limit=1`;
}

/**
 * Lo propio de la cuenta. La API ya filtra por la empresa madre en el mismo
 * statement; aca se repite como defensa, igual que en la bandeja: una fila de
 * otro proveedor nunca llega a la pantalla aunque la respuesta la traiga.
 */
export function ownExport(data: TripExportResponse, providerIds: readonly string[]): TripExportResponse {
  const own = new Set(providerIds);
  return {
    ...data,
    periods: data.periods.filter((period) => own.has(period.providerId)),
    rows: data.rows.filter((row) => own.has(row.providerId)),
  };
}

/** Viajes distintos: con centro de costos, un viaje ocupa una fila por centro. */
export function tripCount(rows: readonly Pick<TripExportRow, "tripId">[]): number {
  return new Set(rows.map((row) => row.tripId)).size;
}

const PERIOD_KIND_LABEL: Record<SettlementPeriodKind, string> = {
  monthly: "mensual",
  biweekly: "quincenal",
  weekly: "semanal",
};

/** "Del 01/10/2026 al 31/10/2026 (mensual)". */
export function periodText(period: Pick<TripExportPeriod, "from" | "to" | "kind">): string {
  return `Del ${formatCalendarDate(period.from)} al ${formatCalendarDate(period.to)} (${PERIOD_KIND_LABEL[period.kind]})`;
}

/** Periodos distintos: dos habilitaciones en la misma planta con el mismo periodo se nombran una vez. */
export function periodTexts(periods: readonly TripExportPeriod[]): string[] {
  return [...new Set(periods.map(periodText))];
}

export const OUTCOME_LABEL: Record<BillableOutcome, string> = {
  completed: "Completado",
  cancelled_by_site: "Cancelado por la planta",
  cancelled_by_ucorp: "Cancelado por UCORP",
  provider_no_departure: "El proveedor no salió",
  unclosed: "Sin cierre operativo",
};

export function serviceLabel(row: Pick<TripExportRow, "vehicleType">): string {
  return VEHICLE_TYPE_LABEL[row.vehicleType];
}

/** Agrupa miles con punto, como se escribe en castellano: 1234 -> "1.234". */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Metros enteros a kilometros con coma decimal y tres decimales:
 * 12345 -> "12,345", 7 -> "0,007". Sin dato (o un valor que no es un entero
 * no negativo) da undefined: nunca se inventa un cero.
 */
export function metersToKm(meters: number | undefined): string | undefined {
  if (meters === undefined || !Number.isSafeInteger(meters) || meters < 0) return undefined;
  const km = Math.floor(meters / 1000);
  const rest = meters % 1000;
  return `${groupThousands(String(km))},${String(rest).padStart(3, "0")}`;
}

const NO_DATA = "sin dato";

/** Km planificados: de Google Routes el largo no se guarda (ADR-026 R3). */
export function plannedKmText(row: Pick<TripExportRow, "plannedMeters" | "plannedDistanceSource">): string {
  if (row.plannedMeters === undefined && row.plannedDistanceSource === "routes") return "Google Routes (no se guarda)";
  return metersToKm(row.plannedMeters) ?? NO_DATA;
}

export function actualKmText(row: Pick<TripExportRow, "actualMeters">): string {
  return metersToKm(row.actualMeters) ?? NO_DATA;
}

/** "12 min", con "(estimada)" cuando sale del GPS y no del conductor. */
export function waitText(row: Pick<TripExportRow, "waitMinutes" | "waitSource">): string {
  if (row.waitMinutes === undefined) return NO_DATA;
  const base = `${String(row.waitMinutes)} min`;
  return row.waitSource === "estimated" ? `${base} (estimada)` : base;
}

/** Asignados / a bordo / ausentes, solo cantidades. */
export function passengersText(row: Pick<TripExportRow, "passengersAssigned" | "passengersBoarded" | "passengersNoShow">): string {
  return `${String(row.passengersAssigned)} / ${String(row.passengersBoarded)} / ${String(row.passengersNoShow)}`;
}

/**
 * Puntos basicos a porcentaje con dos decimales: 3334 -> "33,34 %". Fuera de
 * 0 a 10000 o no entero da undefined.
 */
export function basisPointsToPercent(basisPoints: number | undefined): string | undefined {
  if (basisPoints === undefined || !Number.isSafeInteger(basisPoints) || basisPoints < 0 || basisPoints > 10_000) return undefined;
  const whole = Math.floor(basisPoints / 100);
  const rest = basisPoints % 100;
  return `${String(whole)},${String(rest).padStart(2, "0")} %`;
}

/** "CC-10 · 33,34 %"; sin centro de costos en la fila, undefined. */
export function costCenterText(row: Pick<TripExportRow, "costCenterId" | "costCenterShareBasisPoints">): string | undefined {
  if (row.costCenterId === undefined) return undefined;
  const share = basisPointsToPercent(row.costCenterShareBasisPoints);
  return share === undefined ? row.costCenterId : `${row.costCenterId} · ${share}`;
}

/** Marcas del viaje en castellano, en el orden del contrato. */
export function flagLabels(row: Pick<TripExportRow, "offShift" | "pointToPoint" | "relaunched" | "holiday">): string[] {
  const labels: string[] = [];
  if (row.offShift) labels.push("Fuera de turno");
  if (row.pointToPoint) labels.push("Punto a punto");
  if (row.relaunched) labels.push("Relanzado");
  if (row.holiday) labels.push("Feriado");
  return labels;
}

/** Lo que vale en un nombre de archivo: sin rutas, sin comillas, sin caracteres de control. */
function safeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^[._]+/, "").slice(0, 120);
}

export function fallbackFileName(siteId: string, date: string, format: Exclude<TripExportFormat, "json">): string {
  return safeFileName(`viajes-${siteId}-${date}.${format}`);
}

/**
 * Nombre del archivo desde Content-Disposition (filename* de RFC 5987 o
 * filename), saneado. Sin cabecera, sin nombre util o con otra extension, el
 * nombre por defecto: lo que baja siempre dice que es.
 */
export function fileNameFromDisposition(
  header: string | null,
  siteId: string,
  date: string,
  format: Exclude<TripExportFormat, "json">,
): string {
  const fallback = fallbackFileName(siteId, date, format);
  if (header === null) return fallback;
  let raw: string | undefined;
  const extended = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  if (extended?.[2] !== undefined) {
    try {
      raw = decodeURIComponent(extended[2].trim().replace(/^"|"$/g, ""));
    } catch {
      raw = undefined;
    }
  }
  if (raw === undefined) {
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    raw = plain?.[2] ?? plain?.[1]?.trim();
  }
  if (raw === undefined) return fallback;
  const base = raw.split(/[\\/]/).pop() ?? "";
  const clean = safeFileName(base);
  return clean.toLowerCase().endsWith(`.${format}`) && clean.length > format.length + 1 ? clean : fallback;
}
